"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { socialAPI, handleApiError } from "../../services/api";
import "./SelectFriendModal.css";

const SelectFriendModal = ({ isOpen, onClose, onSelectFriend }) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState("");
  const [friends, setFriends] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load friends when modal opens
  useEffect(() => {
    const loadFriends = async () => {
      if (!isOpen) {
        setFriends([]);
        setSearchQuery("");
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get approved connections (friends)
        // Try getConnections first, fallback to following with status filter
        let response;
        try {
          response = await socialAPI.getConnections({ status: 'approved' });
        } catch (err) {
          // If getConnections doesn't exist or fails, try following endpoint
          // We'll need to get both following and followers with approved status
          const followingResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/v1/users/following?status=approved`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                Accept: "application/json",
              },
            }
          );

          const followersResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/v1/users/followers?status=approved`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                Accept: "application/json",
              },
            }
          );

          const followingData = await followingResponse.json();
          const followersData = await followersResponse.json();

          // Combine and deduplicate
          const followingUsers = followingData.data || [];
          const followersUsers = followersData.data || [];

          const allFriends = [...followingUsers, ...followersUsers];
          const uniqueFriends = allFriends.filter((friend, index, self) =>
            index === self.findIndex((f) => (f.id || f.user_id) === (friend.id || friend.user_id))
          );

          response = { data: { data: uniqueFriends } };
        }

        if (response.data && response.data.data) {
          const transformedFriends = response.data.data.map((friend) => {
            const uid = friend.id || friend.user_id;
            return {
              id: uid,
              name:
                `${friend.first_name || ""} ${friend.last_name || ""}`.trim() ||
                friend.display_name ||
                friend.full_name ||
                friend.username ||
                "Unknown",
              username: friend.username || (uid ? `@user${uid}` : "@user"),
              profileImage: friend.profile_image_url || "/icons/person.png",
            };
          });

          setFriends(transformedFriends);
        } else {
          setFriends([]);
        }
      } catch (err) {
        console.error("Error loading friends:", err);
        setError(t("chat.selectFriend.error") || "Failed to load friends");
        handleApiError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFriends();
  }, [isOpen, t]);

  // Filter friends based on search query
  const filteredFriends = friends.filter((friend) =>
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    friend.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFriendSelect = (friend) => {
    if (onSelectFriend) {
      onSelectFriend(friend);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="select-friend-modal-overlay" onClick={onClose}>
      <div className="select-friend-modal" onClick={(e) => e.stopPropagation()}>
        <div className="select-friend-modal-header">
          <h2 className="select-friend-modal-title">
            {t("chat.selectFriend.title", "Select a Friend")}
          </h2>
          <button className="select-friend-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="select-friend-modal-content">
          <div className="select-friend-search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder={t("chat.selectFriend.searchPlaceholder", "Search friends...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="select-friend-search-input"
            />
          </div>

          <div className="select-friend-list">
            {error ? (
              <div className="select-friend-error">
                <p>{error}</p>
              </div>
            ) : isLoading ? (
              <div className="select-friend-loading">
                <div className="loading-spinner"></div>
                <p>
                  {t("chat.selectFriend.loading", "Loading friends...")}
                </p>
              </div>
            ) : filteredFriends.length > 0 ? (
              filteredFriends.map((friend) => (
                <div
                  key={friend.id}
                  className="select-friend-item"
                  onClick={() => handleFriendSelect(friend)}
                >
                  <img
                    src={friend.profileImage || "/icons/person.png"}
                    alt={friend.name}
                    className="select-friend-avatar"
                    onError={(e) => {
                      e.target.src = "/icons/person.png";
                    }}
                  />
                  <div className="select-friend-info">
                    <span className="select-friend-name">{friend.name}</span>
                  </div>
                  <div className="select-friend-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>
              ))
            ) : (
              <div className="select-friend-empty">
                <p>
                  {searchQuery.trim()
                    ? t("chat.selectFriend.noResults", "No friends found matching your search")
                    : t("chat.selectFriend.empty", "No friends found")}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="select-friend-modal-actions">
          <button className="select-friend-modal-cancel" onClick={onClose}>
            {t("chat.selectFriend.cancel", "Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectFriendModal;

